import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { toast } from "sonner"
import { Camera, Plus, X, Trash2, Columns2, Share2, Loader2 } from "lucide-react"
import {
  listProgressPhotos, signProgressPhotos, uploadProgressPhoto, deleteProgressPhoto,
  getBodyMeasurementsHistory, getWeightHistory,
  type ProgressPhotoRow,
} from "@/lib/supabase"
import { normalizePhoto, renderComparisonImage, PhotoPipelineError, type ComparisonDelta } from "@/lib/photoPipeline"
import { toLocalISODate, formatFr } from "@/lib/utils"
import { SectionTitle, SectionMeta, CardButton, TONE } from "@/components/profile/ui"

interface Props {
  weekCurrent: number
  weeksTotal: number
  /** Nombre de mouvements dont le record dépasse la valeur du test initial du bloc. */
  recordsBeaten: number
}

/**
 * Photos d'évolution.
 *
 * L'app PROPOSE un créneau toutes les 6 semaines (S1, S6, S12 sur un bloc de 12) mais
 * n'interdit rien entre : le dernier emplacement de la bande est toujours un ajout libre.
 * Un créneau non encore atteint n'est pas affiché — proposer S12 en semaine 2 n'aide
 * personne ; seul le prochain créneau dû apparaît, en emplacement vide à remplir.
 */
function slotWeeks(weeksTotal: number): number[] {
  const slots = new Set<number>([1])
  for (let w = 6; w < weeksTotal; w += 6) slots.add(w)
  slots.add(weeksTotal)
  return [...slots].sort((a, b) => a - b)
}

function formatShort(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })
}

export function ProgressPhotosSection({ weekCurrent, weeksTotal, recordsBeaten }: Props) {
  const [photos, setPhotos] = useState<ProgressPhotoRow[]>([])
  const [urls, setUrls] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [viewing, setViewing] = useState<ProgressPhotoRow | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [comparing, setComparing] = useState(false)

  // Le créneau visé par la prochaine sélection de fichier. null = ajout libre.
  const pendingSlot = useRef<number | null>(null)
  const fileInput = useRef<HTMLInputElement | null>(null)

  const load = useCallback(async () => {
    try {
      const rows = await listProgressPhotos()
      setPhotos(rows)
      // Les URL signées expirent : on les regénère à chaque chargement de la section,
      // jamais on ne les persiste.
      setUrls(await signProgressPhotos(rows.map((p) => p.storage_path)))
    } catch (err) {
      console.error("[ProgressPhotosSection] chargement échoué :", err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const pickFor = (slot: number | null) => {
    pendingSlot.current = slot
    fileInput.current?.click()
  }

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Réinitialisé tout de suite : sans ça, reprendre deux fois la même photo
    // ne redéclencherait pas l'événement change.
    e.target.value = ""
    if (!file || uploading) return

    setUploading(true)
    try {
      const { blob, width, height } = await normalizePhoto(file)
      await uploadProgressPhoto(blob, {
        takenOn: toLocalISODate(new Date()),
        programWeek: weekCurrent,
        slotWeek: pendingSlot.current,
        width,
        height,
      })
      await load()
      toast.success("Photo ajoutée")
    } catch (err) {
      const msg = err instanceof PhotoPipelineError
        ? err.message
        : err instanceof Error ? err.message : "Envoi échoué"
      toast.error("Photo non ajoutée", { description: msg })
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async () => {
    if (!viewing || deleting) return
    setDeleting(true)
    try {
      await deleteProgressPhoto(viewing)
      setViewing(null)
      await load()
      toast.success("Photo supprimée")
    } catch (err) {
      toast.error("Suppression échouée", { description: err instanceof Error ? err.message : undefined })
    } finally {
      setDeleting(false)
    }
  }

  // ── Bande : créneaux dus (remplis ou à prendre) + photos libres + ajout libre ──
  const band = useMemo(() => {
    const bySlot = new Map<number, ProgressPhotoRow>()
    const free: ProgressPhotoRow[] = []
    for (const p of photos) {
      if (p.slot_week != null && !bySlot.has(p.slot_week)) bySlot.set(p.slot_week, p)
      else free.push(p)
    }

    type Item =
      | { kind: "photo"; photo: ProgressPhotoRow; label: string }
      | { kind: "slot"; week: number }

    const items: Item[] = []
    for (const week of slotWeeks(weeksTotal)) {
      const photo = bySlot.get(week)
      if (photo) items.push({ kind: "photo", photo, label: `SEM. ${week}` })
      else if (week <= weekCurrent) items.push({ kind: "slot", week })
    }
    for (const p of free) {
      items.push({ kind: "photo", photo: p, label: p.program_week ? `SEM. ${p.program_week}` : "LIBRE" })
    }
    return items
  }, [photos, weekCurrent, weeksTotal])

  const canCompare = photos.length >= 2

  const handleCompare = async () => {
    if (comparing || !canCompare) return
    setComparing(true)
    try {
      const first = photos[0]
      const last = photos[photos.length - 1]
      const [firstUrl, lastUrl] = [urls[first.storage_path], urls[last.storage_path]]
      if (!firstUrl || !lastUrl) throw new Error("Photos indisponibles, recharge la page.")

      const blob = await renderComparisonImage({
        title: "Avant / Après",
        beforeUrl: firstUrl,
        afterUrl: lastUrl,
        beforeCaption: formatShort(first.taken_on),
        afterCaption: formatShort(last.taken_on),
        deltas: await buildDeltas(first.taken_on, last.taken_on, recordsBeaten),
      })

      const file = new File([blob], "zeyone-avant-apres.jpg", { type: "image/jpeg" })
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "Mon avant / après" })
      } else {
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = file.name
        a.click()
        URL.revokeObjectURL(url)
        toast.success("Image enregistrée")
      }
    } catch (err) {
      // L'utilisateur qui ferme la feuille de partage lève une AbortError : ce n'est pas une panne.
      if (err instanceof DOMException && err.name === "AbortError") return
      toast.error("Comparaison indisponible", { description: err instanceof Error ? err.message : undefined })
    } finally {
      setComparing(false)
    }
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}
      className="mt-6 relative z-10"
    >
      <div className="px-4">
        <SectionTitle label="Mon évolution" right={<SectionMeta>toutes les 6 sem.</SectionMeta>} />
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
      />

      {loading ? (
        <p className="px-5 text-[12px] text-gray-500">Chargement…</p>
      ) : (
        <div className="flex gap-2.5 overflow-x-auto px-4 pb-1" style={{ scrollSnapType: "x mandatory" }}>
          {band.map((item) =>
            item.kind === "photo" ? (
              <button
                key={item.photo.id}
                onClick={() => setViewing(item.photo)}
                className="shrink-0 w-[112px] h-[150px] rounded-[17px] overflow-hidden relative cursor-pointer active:scale-[0.97] transition-transform"
                style={{ border: "1px solid rgba(30,41,59,0.55)", scrollSnapAlign: "start" }}
              >
                {urls[item.photo.storage_path] ? (
                  <img
                    src={urls[item.photo.storage_path]}
                    alt={`Photo ${item.label}`}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <span className="absolute inset-0" style={{ background: "linear-gradient(160deg,#1c2c40,#0b1320 70%)" }} />
                )}
                <span
                  className="absolute inset-x-0 bottom-0 px-2 py-2 text-left"
                  style={{ background: "linear-gradient(to top, rgba(4,7,13,0.92), transparent)" }}
                >
                  <span className="block text-[10px] font-semibold text-gray-300 tnum" style={{ letterSpacing: 1 }}>{item.label}</span>
                  <span className="block text-[10.5px] text-gray-400 tnum">{formatShort(item.photo.taken_on)}</span>
                </span>
              </button>
            ) : (
              <button
                key={`slot-${item.week}`}
                onClick={() => pickFor(item.week)}
                disabled={uploading}
                className="shrink-0 w-[112px] h-[150px] rounded-[17px] flex flex-col items-center justify-center gap-2 cursor-pointer active:scale-[0.97] transition-transform disabled:opacity-50"
                style={{ border: "1px dashed rgba(255,255,255,0.13)", background: "rgba(13,20,32,0.6)", scrollSnapAlign: "start" }}
              >
                <span className="w-9 h-9 rounded-full grid place-items-center" style={{ border: "1px dashed rgba(255,255,255,0.18)" }}>
                  {uploading ? <Loader2 className="w-4 h-4 text-gray-500 animate-spin" /> : <Camera className="w-4 h-4 text-gray-500" />}
                </span>
                <span className="text-[10px] font-semibold text-gray-400 tnum" style={{ letterSpacing: 1 }}>SEM. {item.week}</span>
                <span className="text-[10.5px] text-gray-600">À prendre</span>
              </button>
            )
          )}

          <button
            onClick={() => pickFor(null)}
            disabled={uploading}
            className="shrink-0 w-[112px] h-[150px] rounded-[17px] flex flex-col items-center justify-center gap-2 cursor-pointer active:scale-[0.97] transition-transform disabled:opacity-50"
            style={{ border: "1px dashed rgba(255,255,255,0.13)", background: "rgba(13,20,32,0.6)", scrollSnapAlign: "start" }}
          >
            <span className="w-9 h-9 rounded-full grid place-items-center" style={{ border: "1px dashed rgba(255,255,255,0.18)" }}>
              {uploading ? <Loader2 className="w-4 h-4 text-gray-500 animate-spin" /> : <Plus className="w-4 h-4 text-gray-500" />}
            </span>
            <span className="text-[10px] font-semibold text-gray-400" style={{ letterSpacing: 1 }}>LIBRE</span>
            <span className="text-[10.5px] text-gray-600">Ajouter</span>
          </button>
        </div>
      )}

      {canCompare && (
        <div className="px-4 mt-3">
          <CardButton onClick={handleCompare}>
            <div className="flex items-center gap-3 p-3.5">
              <span
                className="w-[34px] h-[34px] rounded-[11px] grid place-items-center shrink-0"
                style={{ background: TONE.violet.bg, border: `1px solid ${TONE.violet.border}`, color: TONE.violet.fg }}
              >
                {comparing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Columns2 className="w-4 h-4" />}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[14px] font-semibold text-white">Comparer avant / après</span>
                <span className="block text-[11.5px] text-gray-500 mt-0.5">Image partageable avec tes écarts chiffrés</span>
              </span>
              <Share2 className="w-4 h-4 text-gray-600 shrink-0" />
            </div>
          </CardButton>
        </div>
      )}

      <p className="text-[11.5px] text-gray-600 leading-relaxed mt-2.5 px-5">
        Tes photos sont privées : stockage chiffré, jamais d'adresse publique, et elles sont
        effacées avec ton compte.
      </p>

      {/* Visionneuse plein écran */}
      <AnimatePresence>
        {viewing && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-[4px]"
              onClick={() => !deleting && setViewing(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}
              className="fixed z-[100] inset-x-4 top-1/2 -translate-y-1/2 max-w-[420px] mx-auto"
            >
              {urls[viewing.storage_path] && (
                <img
                  src={urls[viewing.storage_path]}
                  alt=""
                  className="w-full rounded-[22px]"
                  style={{ maxHeight: "68vh", objectFit: "contain", background: "#04070d" }}
                />
              )}
              <div className="flex items-center gap-2 mt-3">
                <span className="text-[12px] text-gray-400 tnum flex-1">
                  {formatShort(viewing.taken_on)}
                  {viewing.program_week ? ` · semaine ${viewing.program_week}` : ""}
                </span>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-3 py-2 rounded-xl text-[13px] font-semibold flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  style={{ background: TONE.red.bg, border: `1px solid ${TONE.red.border}`, color: "#f87171" }}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Supprimer
                </button>
                <button
                  onClick={() => setViewing(null)}
                  className="w-9 h-9 rounded-xl grid place-items-center cursor-pointer"
                  style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.section>
  )
}

/**
 * Écarts affichés sur l'image partageable : poids, tour de taille, records battus.
 * Chaque écart est mesuré entre le relevé le plus proche de la première photo et le plus
 * récent — pas entre deux dates arbitraires. Un écart sans données des deux côtés est
 * simplement omis : mieux vaut deux tuiles justes que trois dont une à zéro.
 */
async function buildDeltas(
  fromIso: string,
  toIso: string,
  recordsBeaten: number
): Promise<ComparisonDelta[]> {
  const out: ComparisonDelta[] = []
  try {
    const [weights, measures] = await Promise.all([
      getWeightHistory(3650),
      getBodyMeasurementsHistory(3650),
    ])

    const weightFrom = nearest(weights.map((w) => ({ date: w.date, value: w.weight })), fromIso)
    const weightTo = nearest(weights.map((w) => ({ date: w.date, value: w.weight })), toIso)
    if (weightFrom != null && weightTo != null) {
      out.push({ label: "Poids", value: signed(weightTo - weightFrom, "kg") })
    }

    const waist = measures
      .filter((m) => typeof m.waist_cm === "number")
      .map((m) => ({ date: m.date, value: m.waist_cm as number }))
    const waistFrom = nearest(waist, fromIso)
    const waistTo = nearest(waist, toIso)
    if (waistFrom != null && waistTo != null) {
      out.push({ label: "Tour de taille", value: signed(waistTo - waistFrom, "cm") })
    }
  } catch (err) {
    console.error("[buildDeltas] écarts indisponibles :", err)
  }

  if (recordsBeaten > 0) {
    out.push({ label: "Records", value: `${recordsBeaten} battu${recordsBeaten > 1 ? "s" : ""}` })
  }
  return out
}

function nearest(rows: { date: string; value: number }[], target: string): number | null {
  if (rows.length === 0) return null
  const t = new Date(target).getTime()
  let best = rows[0]
  let bestGap = Math.abs(new Date(best.date).getTime() - t)
  for (const r of rows) {
    const gap = Math.abs(new Date(r.date).getTime() - t)
    if (gap < bestGap) { best = r; bestGap = gap }
  }
  return best.value
}

function signed(delta: number, unit: string): string {
  const sign = delta > 0 ? "+" : delta < 0 ? "−" : ""
  return `${sign}${formatFr(Math.abs(delta), 1)} ${unit}`
}
